import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  IconButton,
} from '@socialchorus/shared-ui-components';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { Feature, getFeatureFlag } from '../../../models/features/features';
import CommentAttachmentActionWrapper from './comment-attachment-action-wrapper';

interface CommentFormActionsProps {
  onEmojiIconClick: () => void;
  onMentionIconClick: () => void;
  onAttachmentClick: (file: File) => void;
  buttonsOutsideMenu: number; // How many buttons to show outside the more menu
}

interface ActionButton {
  key: string;
  icon: string;
  title: string;
  onClick: () => void;
  isAttachment?: boolean;
}

const CommentFormActions: React.FC<CommentFormActionsProps> = ({
  onEmojiIconClick,
  onMentionIconClick,
  onAttachmentClick,
  buttonsOutsideMenu,
}) => {
  const { t } = useTranslation();
  const commentAttachmentsEnabled = usePatronSelector((state) =>
    getFeatureFlag(state, Feature.COMMENT_ATTACHMENTS_ENABLED)
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Define all available actions
  const allActions: ActionButton[] = [
    {
      key: 'emoji',
      icon: 'insert_emoticon',
      title: t('comments.actions.emoji'),
      onClick: onEmojiIconClick,
    },
    {
      key: 'mention',
      icon: 'alternate_email',
      title: t('comments.actions.mention'),
      onClick: onMentionIconClick,
    },
  ];

  // Add attachment action if enabled
  if (commentAttachmentsEnabled) {
    allActions.push({
      key: 'attachment',
      icon: 'photo',
      title: t('comments.actions.attachments'),
      onClick: () => undefined, // Handled specially by the wrapper
      isAttachment: true,
    });
  }

  // Calculate how many actions to show outside vs inside menu
  // Account for the more button taking up one slot
  let actionsOutside: ActionButton[];
  let actionsInMenu: ActionButton[];

  if (buttonsOutsideMenu <= 1) {
    // Show only the more menu
    actionsOutside = [];
    actionsInMenu = allActions;
  } else if (buttonsOutsideMenu >= allActions.length) {
    // Show all actions outside, no more menu needed
    actionsOutside = allActions;
    actionsInMenu = [];
  } else {
    // Show (buttonsOutsideMenu - 1) actions outside + more button
    // This accounts for the more button taking up one slot
    actionsOutside = allActions.slice(0, buttonsOutsideMenu - 1);
    actionsInMenu = allActions.slice(buttonsOutsideMenu - 1);
  }

  const renderButton = (action: ActionButton) => {
    if (action.isAttachment) {
      return (
        <CommentAttachmentActionWrapper
          key={action.key}
          onFileSelect={onAttachmentClick}
        >
          <IconButton
            type="button"
            size="compact"
            aria-label={action.title}
            iconName={action.icon}
          />
        </CommentAttachmentActionWrapper>
      );
    }

    return (
      <IconButton
        type="button"
        key={action.key}
        size="compact"
        onClick={action.onClick}
        aria-label={action.title}
        iconName={action.icon}
      />
    );
  };

  const renderMenuItemButton = (action: ActionButton) => {
    if (action.isAttachment) {
      return (
        <CommentAttachmentActionWrapper
          key={action.key}
          onFileSelect={onAttachmentClick}
        >
          <DropdownMenuItem>
            <Icon>{action.icon}</Icon>
            <span>{action.title}</span>
          </DropdownMenuItem>
        </CommentAttachmentActionWrapper>
      );
    }

    return (
      <DropdownMenuItem key={action.key} onClick={action.onClick}>
        <Icon>{action.icon}</Icon>
        <span>{action.title}</span>
      </DropdownMenuItem>
    );
  };

  return (
    <>
      {/* Render buttons outside the menu */}
      {actionsOutside.map((action) => renderButton(action))}

      {/* Render more menu if there are actions to put inside */}
      {actionsInMenu.length > 0 && (
        <DropdownMenu
          open={isMenuOpen}
          onOpenChange={setIsMenuOpen}
          modal={false}
        >
          <DropdownMenuTrigger asChild>
            <IconButton
              aria-label={t('comments.actions.label')}
              iconName="more_horiz"
              size="compact"
            />
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="start"
            className="comment-form-actions-menu"
          >
            {actionsInMenu.map((action) => renderMenuItemButton(action))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
};

export default CommentFormActions;
